import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HASHTAG } from "@/lib/wedding-data";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function GuestPhotos() {
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photos`);
      setPhotos(res.data.photos || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const upload = async () => {
    if (!file) {
      toast.error("Choose a photo first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      await axios.post(`${API}/photos`, fd);
      toast.success("Photo received - it will appear on the wall once the couple approves it.");
      setFile(null);
      setName("");
      if (fileRef.current) fileRef.current.value = "";
      fetchPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section
      id="share"
      data-testid="share-section"
      className="scroll-mt-24 py-28 md:py-36 bg-[#5D171E] text-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#E1C699] mb-6">
              Guest Photo Wall
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Share <span className="italic text-[#E1C699]">your view</span>
            </h2>
            <p className="mt-6 text-base text-[#FDFBF7]/70 max-w-md">
              The best moments are the ones you capture. Upload your favourite
              photographs of the couple - and after the wedding, of the
              celebrations - and tag them {HASHTAG}.
            </p>

            <div className="gold-frame mt-10 rounded-2xl bg-[#FDFBF7] text-[#1A1A1A] border border-[#D4AF37]/40 p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                  Your Name (optional)
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="So we can thank you"
                  data-testid="share-name-input"
                  className="bg-white/70 border-[#D4AF37]/40 focus-visible:ring-[#5D171E] placeholder:text-[#1A1A1A]/35"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5D171E]">
                  Photo
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  data-testid="share-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="share-choose-button"
                  className="w-full rounded-xl border border-dashed border-[#D4AF37] bg-white/60 px-4 py-8 flex flex-col items-center gap-2 text-[#5D171E] hover:bg-[#D4AF37]/10 transition-colors duration-300"
                >
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-sm">
                    {file ? file.name : "Tap to choose a photo (max 15 MB)"}
                  </span>
                </button>
              </div>
              <Button
                onClick={upload}
                disabled={uploading}
                data-testid="share-upload-button"
                className="w-full rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.25em] py-6 hover:bg-[#471016] transition-colors duration-300"
              >
                {uploading ? "Uploading..." : "Share Photo"}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-8">
              <Camera className="h-4 w-4 text-[#E1C699]" />
              <h3
                className="text-xs uppercase tracking-[0.3em] text-[#E1C699]"
                data-testid="guest-photos-count"
              >
                {photos.length === 0
                  ? "The wall awaits its first photo"
                  : `${photos.length} ${photos.length === 1 ? "photo" : "photos"} shared`}
              </h3>
            </div>
            {photos.length === 0 ? (
              <div
                data-testid="guest-photos-empty"
                className="rounded-2xl border border-dashed border-[#D4AF37]/40 px-8 py-24 text-center"
              >
                <p className="font-serif italic text-2xl text-[#E1C699]/80">
                  Every love story looks different through your eyes
                </p>
                <p className="mt-3 text-sm text-[#FDFBF7]/60">
                  Upload the first photograph and start the wall.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((p, i) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
                    data-testid={`guest-photo-${i}`}
                    className="group relative overflow-hidden rounded-2xl aspect-square border border-[#D4AF37]/30"
                  >
                    <img
                      src={`${BACKEND}/api/photos/file/${p.storage_path}`}
                      alt={p.uploader_name ? `Shared by ${p.uploader_name}` : "Guest photo"}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {p.uploader_name && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8">
                        <p className="text-xs uppercase tracking-[0.15em] text-[#FDFBF7]/90">
                          {p.uploader_name}
                        </p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
